import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { API_URL } from '../config/api';

export interface KnowledgeChatMessage {
  id: string;
  tempId?: string;
  content: string;
  isFromAI: boolean;
  timestamp: string;
}

export interface KnowledgeContext {
  title: string;
  itemType: string;
  content: string;
}

export enum KnowledgeChatStatus {
  Idle = 0,
  AITyping = 1,
  Processing = 2
}

export interface KnowledgeChatStatusEvent {
  knowledgeItemId: string;
  status: KnowledgeChatStatus;
}

// Интерфейсы для потоковых событий
export interface MessageChunkEvent {
  messageId: string;
  chunk: string;
  fullContent: string;
}

export interface MessageFinalizedEvent {
  messageId: string;
  finalContent: string;
}

export class KnowledgeChatService {
  private connection: HubConnection | null = null;
  private isConnected = false;
  private isReconnecting = false;
  
  // Обработчики событий
  private messageHandlers: ((message: KnowledgeChatMessage) => void)[] = [];
  private statusHandlers: ((status: KnowledgeChatStatusEvent) => void)[] = [];
  private errorHandlers: ((error: string) => void)[] = [];
  private messageDeletedHandlers: ((messageId: string) => void)[] = [];
  private chatClearedHandlers: ((data: { knowledgeItemId: string }) => void)[] = [];
  private reconnectedHandlers: (() => void)[] = [];
  private disconnectedHandlers: (() => void)[] = [];
  
  // Новые обработчики для потоковых событий
  private messageChunkHandlers: ((chunk: MessageChunkEvent) => void)[] = [];
  private messageFinalizedHandlers: ((finalized: MessageFinalizedEvent) => void)[] = [];

  constructor() {
    this.initializeConnection();
  }

  private initializeConnection() {
    const apiUrl = API_URL;
    // Убираем /api из URL для SignalR хабов
    const hubUrl = `${apiUrl.replace('/api', '')}/knowledgeChatHub`;
    
    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => {
          const currentToken = localStorage.getItem('auth_token');
          return currentToken || '';
        }
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    if (!this.connection) return;

    // Обработчик получения сообщений
    this.connection.on('MessageReceived', (message: KnowledgeChatMessage) => {
      this.messageHandlers.forEach(handler => handler(message));
    });

    // Обработчик изменения статуса
    this.connection.on('StatusChanged', (statusEvent: KnowledgeChatStatusEvent) => {
      this.statusHandlers.forEach(handler => handler(statusEvent));
    });

    // Обработчик ошибок
    this.connection.on('Error', (error: string) => {
      this.errorHandlers.forEach(handler => handler(error));
    });

    // Обработчик удаления сообщения
    this.connection.on('MessageDeleted', (data: { messageId: string }) => {
      this.messageDeletedHandlers.forEach(handler => handler(data.messageId));
    });

    // Обработчик очистки чата
    this.connection.on('ChatHistoryCleared', (data: { knowledgeItemId: string }) => {
      this.chatClearedHandlers.forEach(handler => handler(data));
    });

    // Обработчик фрагментов сообщений (потоковый режим)
    this.connection.on('MessageChunk', (chunk: MessageChunkEvent) => {
      this.messageChunkHandlers.forEach(handler => handler(chunk));
    });

    // Обработчик финализации сообщения (потоковый режим)
    this.connection.on('MessageFinalized', (finalized: MessageFinalizedEvent) => {
      this.messageFinalizedHandlers.forEach(handler => handler(finalized));
    });

    // Обработчик переподключения
    this.connection.onreconnected(() => {
      this.isConnected = true;
      this.isReconnecting = false;
      this.reconnectedHandlers.forEach(handler => handler());
    });

    // Обработчик начала переподключения
    this.connection.onreconnecting(() => {
      this.isReconnecting = true;
    });

    // Обработчик отключения
    this.connection.onclose(() => {
      this.isConnected = false;
      this.isReconnecting = false;
      this.disconnectedHandlers.forEach(handler => handler());
    });
  }

  // Подключение к SignalR Hub
  public async connect(): Promise<void> {
    if (this.isConnected || !this.connection) {
      return;
    }

    try {
      await this.connection.start();
      this.isConnected = true;
    } catch (error) {
      this.isConnected = false;
      throw error;
    }
  }

  // Отключение от SignalR Hub
  public async disconnect(): Promise<void> {
    if (!this.isConnected || !this.connection) return;

    try {
      await this.connection.stop();
      this.isConnected = false;
    } catch (error) {
      console.error('Error disconnecting from knowledge chat hub:', error);
    }
  }

  // Отправка сообщения
  public async sendMessage(
    knowledgeItemId: string, 
    message: string,
    tempId: string,
    knowledgeContext: KnowledgeContext
  ): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to knowledge chat hub');
    }

    try {
      await this.connection.invoke('SendMessage', knowledgeItemId, message, tempId, knowledgeContext);
    } catch (error) {
      throw error;
    }
  }

  // Отправка сообщения в потоковом режиме
  public async sendMessageStream(
    knowledgeItemId: string, 
    message: string,
    tempId: string,
    knowledgeContext: KnowledgeContext
  ): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to knowledge chat hub');
    }

    try {
      await this.connection.invoke('SendMessageStream', knowledgeItemId, message, tempId, knowledgeContext);
    } catch (error) {
      throw error;
    }
  }

  // Очистка истории чата
  public async clearChatHistory(knowledgeItemId: string): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to knowledge chat hub');
    }

    try {
      await this.connection.invoke('ClearChatHistory', knowledgeItemId);
    } catch (error) {
      throw error;
    }
  }

  // Получение истории чата
  public async getChatHistory(knowledgeItemId: string): Promise<KnowledgeChatMessage[]> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to knowledge chat hub');
    }

    try {
      const history = await this.connection.invoke('GetChatHistory', knowledgeItemId);
      return Array.isArray(history) ? history : [];
    } catch (error) {
      throw error;
    }
  }

  // Удаление сообщения
  public async deleteMessage(messageId: string): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('Not connected to knowledge chat hub');
    }

    try {
      await this.connection.invoke('DeleteMessage', messageId);
    } catch (error) {
      throw error;
    }
  }

  // Подписка на получение сообщений
  public onMessageReceived(handler: (message: KnowledgeChatMessage) => void): () => void {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  // Подписка на изменение статуса
  public onStatusChanged(handler: (status: KnowledgeChatStatusEvent) => void): () => void {
    this.statusHandlers.push(handler);
    return () => {
      const index = this.statusHandlers.indexOf(handler);
      if (index > -1) {
        this.statusHandlers.splice(index, 1);
      }
    };
  }

  // Подписка на ошибки
  public onError(handler: (error: string) => void): () => void {
    this.errorHandlers.push(handler);
    return () => {
      const index = this.errorHandlers.indexOf(handler);
      if (index > -1) {
        this.errorHandlers.splice(index, 1);
      }
    };
  }

  // Подписка на удаление сообщений
  public onMessageDeleted(handler: (messageId: string) => void): () => void {
    this.messageDeletedHandlers.push(handler);
    return () => {
      const index = this.messageDeletedHandlers.indexOf(handler);
      if (index > -1) {
        this.messageDeletedHandlers.splice(index, 1);
      }
    };
  }

  // Подписка на очистку чата
  public onChatCleared(handler: (data: { knowledgeItemId: string }) => void): () => void {
    this.chatClearedHandlers.push(handler);
    return () => {
      const index = this.chatClearedHandlers.indexOf(handler);
      if (index > -1) {
        this.chatClearedHandlers.splice(index, 1);
      }
    };
  }

  // Подписка на переподключение
  public onReconnected(handler: () => void): () => void {
    this.reconnectedHandlers.push(handler);
    return () => {
      const index = this.reconnectedHandlers.indexOf(handler);
      if (index > -1) {
        this.reconnectedHandlers.splice(index, 1);
      }
    };
  }

  // Подписка на отключение
  public onDisconnected(handler: () => void): () => void {
    this.disconnectedHandlers.push(handler);
    return () => {
      const index = this.disconnectedHandlers.indexOf(handler);
      if (index > -1) {
        this.disconnectedHandlers.splice(index, 1);
      }
    };
  }

  // Подписка на фрагменты сообщений (потоковый режим)
  public onMessageChunk(handler: (chunk: MessageChunkEvent) => void): () => void {
    this.messageChunkHandlers.push(handler);
    return () => {
      const index = this.messageChunkHandlers.indexOf(handler);
      if (index > -1) {
        this.messageChunkHandlers.splice(index, 1);
      }
    };
  }

  // Подписка на финализацию сообщения (потоковый режим)
  public onMessageFinalized(handler: (finalized: MessageFinalizedEvent) => void): () => void {
    this.messageFinalizedHandlers.push(handler);
    return () => {
      const index = this.messageFinalizedHandlers.indexOf(handler);
      if (index > -1) {
        this.messageFinalizedHandlers.splice(index, 1);
      }
    };
  }

  // Геттеры для состояния соединения
  public get connected(): boolean {
    return this.isConnected;
  }

  public get reconnecting(): boolean {
    return this.isReconnecting;
  }
} 